import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import './Layout.css'
import Pipeline from './Pipeline'
import Schools from './Schools'
import Coaches from './Coaches'
import MyPage from './MyPage'
import Settings from './Settings'
import Onboarding from './Onboarding'
import Tour from './Tour'

const NAV_ITEMS = [
  {
    id: 'pipeline',
    label: 'Pipeline',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12H2"/>
        <path d="M5 5H2v14h3"/>
        <path d="M19 5h3v14h-3"/>
        <rect x="5" y="3" width="14" height="4" rx="1"/>
        <rect x="5" y="10" width="9" height="4" rx="1"/>
        <rect x="5" y="17" width="6" height="4" rx="1"/>
      </svg>
    ),
  },
  {
    id: 'schools',
    label: 'Schools',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5z"/>
        <path d="M9 21V12h6v9"/>
      </svg>
    ),
  },
  {
    id: 'coaches',
    label: 'Coaches',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="7" r="3"/>
        <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        <path d="M21 21v-2a4 4 0 0 0-3-3.85"/>
      </svg>
    ),
  },
  {
    id: 'mypage',
    label: 'My Page',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="2"/>
        <circle cx="9" cy="10" r="2.5"/>
        <path d="M4 20c0-2.5 2-4 5-4s5 1.5 5 4"/>
        <line x1="15" y1="8" x2="20" y2="8"/>
        <line x1="15" y1="12" x2="19" y2="12"/>
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
  },
]

export default function Layout({ user }) {
  const location  = useLocation()
  const navigate  = useNavigate()
  const activePage = NAV_ITEMS.find(item => location.pathname === `/${item.id}`)?.id || 'pipeline'

  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showTour, setShowTour]         = useState(false)

  // ── Inactivity sign-out (30 min) ──────────────────────────────────
  useEffect(() => {
    const TIMEOUT = 30 * 60 * 1000
    let timer

    function reset() {
      clearTimeout(timer)
      timer = setTimeout(() => supabase.auth.signOut(), TIMEOUT)
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach(e => window.addEventListener(e, reset, { passive: true }))
    reset()

    return () => {
      clearTimeout(timer)
      events.forEach(e => window.removeEventListener(e, reset))
    }
  }, [])

  useEffect(() => {
    async function checkOnboarding() {
      const { data } = await supabase
        .from('profiles')
        .select('full_name, onboarding_complete, tour_complete')
        .eq('id', user.id)
        .single()
      if (!data?.onboarding_complete || !data?.full_name) {
        setShowOnboarding(true)
      } else if (!data?.tour_complete) {
        setTimeout(() => setShowTour(true), 1000)
      }
    }
    checkOnboarding()
  }, [user.id])

  // Called when onboarding finishes — start tour for new users
  function handleOnboardingComplete() {
    setShowOnboarding(false)
    setTimeout(() => setShowTour(true), 1000)
  }

  // Called when tour finishes or is skipped
  async function handleTourComplete() {
    setShowTour(false)
    await supabase
      .from('profiles')
      .update({ tour_complete: true })
      .eq('id', user.id)
  }

  // Called from Settings → Replay Tour button
  async function handleReplayTour() {
    await supabase
      .from('profiles')
      .update({ tour_complete: false })
      .eq('id', user.id)
    navigate('/pipeline')
    setTimeout(() => setShowTour(true), 300)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <svg width="40" height="45" viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="64" height="72" rx="10" fill="#0D2340" />
            <rect width="64" height="10" fill="#1D9E75" />
            <rect width="64" height="5" y="5" fill="#1D9E75" />
            <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="34" fontWeight="bold" fill="white">C</text>
            <text x="32" y="64" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#1D9E75">HQ</text>
          </svg>
          <span className="sidebar-wordmark">
            Commit<span className="sidebar-wordmark-hq">HQ</span>
          </span>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              className={`sidebar-nav-item${activePage === item.id ? ' active' : ''}`}
              onClick={() => navigate(`/${item.id}`)}
              data-tour={item.id === 'mypage' ? 'nav-mypage' : item.id === 'schools' ? 'nav-schools' : undefined}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
            <span className="sidebar-email">{user.email}</span>
          </div>
          <button className="sidebar-signout" onClick={handleSignOut}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="main-header">
          <h2 className="main-title">
            {NAV_ITEMS.find(i => i.id === activePage)?.label}
          </h2>
        </header>
        <div className="main-body">
          {activePage === 'pipeline' ? (
            <Pipeline user={user} />
          ) : activePage === 'schools' ? (
            <Schools user={user} />
          ) : activePage === 'coaches' ? (
            <Coaches user={user} />
          ) : activePage === 'mypage' ? (
            <MyPage user={user} />
          ) : activePage === 'settings' ? (
            <Settings user={user} onReplayTour={handleReplayTour} />
          ) : (
            <div className="placeholder-panel">
              <p>{NAV_ITEMS.find(i => i.id === activePage)?.label} coming soon.</p>
            </div>
          )}
        </div>
      </main>

      {/* ── Mobile bottom tab bar ── */}
      <nav className="mobile-tab-bar">
        {NAV_ITEMS.map(item => (
          <button
            key={item.id}
            className={`mobile-tab-btn${activePage === item.id ? ' active' : ''}`}
            onClick={() => navigate(`/${item.id}`)}
            data-tour={item.id === 'mypage' ? 'nav-mypage' : item.id === 'schools' ? 'nav-schools' : undefined}
          >
            <span className="mobile-tab-icon">{item.icon}</span>
            <span className="mobile-tab-label">{item.label}</span>
          </button>
        ))}
      </nav>

      {showOnboarding && (
        <Onboarding user={user} onComplete={handleOnboardingComplete} />
      )}

      <Tour active={showTour} onComplete={handleTourComplete} />
    </div>
  )
}
